import { z } from "zod";
import { MAX_ORTSPFAD } from "./ort.js";
import { VERWANDTSCHAFTSARTEN, GESCHLECHTER, FAMILIENROLLEN, VERBINDUNGSARTEN, EREIGNISARTEN, EREIGNISROLLEN, MEDIENZIELE, BENUTZERROLLEN, NAMENSARTEN, } from "./typen.js";
import { GENAUIGKEITEN } from "./datum.js";
export const zGeschlecht = z.enum(GESCHLECHTER);
export const zFamilienrolle = z.enum(FAMILIENROLLEN);
export const zVerbindungsart = z.enum(VERBINDUNGSARTEN);
export const zEreignisart = z.enum(EREIGNISARTEN);
export const zEreignisrolle = z.enum(EREIGNISROLLEN);
export const zMedienziel = z.enum(MEDIENZIELE);
export const zBenutzerrolle = z.enum(BENUTZERROLLEN);
export const zGenauigkeit = z.enum(GENAUIGKEITEN);
export const zNamensart = z.enum(NAMENSARTEN);
const isoDatum = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Erwartet YYYY-MM-DD");
export const zUnscharfesDatum = z.object({
    von: isoDatum.nullable(),
    bis: isoDatum.nullable(),
    genauigkeit: zGenauigkeit,
    text: z.string().max(100).nullable(),
});
/**
 * Ein abweichender Name, wie ihn die Maske schickt.
 *
 * Vor- und Familienname sind beide freigestellt, aber mindestens einer muss
 * dastehen: bei einer Schreibvariante aendert sich oft nur der Familienname
 * („Huber Josef" -> „Hueber Josef"), und die andere Haelfte dann leer zu
 * lassen ist die ehrliche Angabe.
 *
 * `notiz` ist die Fundstelle in Worten - „Taufbuch Echsenbach 1834, fol. 22".
 * Bewusst Freitext und keine Verknuepfung in die Quellenverwaltung: die
 * Maske wuerde sonst zweistufig verschachtelt (Person -> Namen -> Quellen)
 * und waere zum Eintippen zaeh.
 *
 * Wer es spaeter foermlich will, kann Verweise nicht einfach daranhaengen:
 * `namenSetzen` ersetzt die Zeilen bei jedem Speichern, die IDs sind also
 * nicht stabil, und angehaengte Verweise waeren nach der naechsten
 * Aenderung verwaist. Dafuer muesste vorher abgeglichen statt ersetzt
 * werden.
 */
export const zPersonenname = z
    .object({
    vorname: z.string().trim().max(80).nullish(),
    famname: z.string().trim().max(80).nullish(),
    art: zNamensart.default("variante"),
    notiz: z.string().trim().max(500).nullish(),
})
    .refine((n) => Boolean(n.vorname || n.famname), {
    message: "Bitte einen Vor- oder Familiennamen eintragen.",
    path: ["famname"],
});
export const zPersonEingabe = z.object({
    vorname: z.string().trim().max(80).default(""),
    famname: z.string().trim().max(80).default(""),
    rufname: z.string().trim().max(80).nullish(),
    maedchenname: z.string().trim().max(80).nullish(),
    geschlecht: zGeschlecht.default("unbekannt"),
    geburt: zUnscharfesDatum.nullish(),
    tod: zUnscharfesDatum.nullish(),
    geburtsortId: z.number().int().positive().nullish(),
    wohnortId: z.number().int().positive().nullish(),
    /**
     * Die Maske schickt Ortsnamen, keine IDs - wie die Ereignismaske auch.
     * Ein Ortsregister pflegen zu muessen, bevor man eine Person anlegen
     * darf, waere genau die Huerde, an der so etwas im Alltag scheitert.
     */
    geburtsortName: z.string().trim().max(MAX_ORTSPFAD).nullish(),
    wohnortName: z.string().trim().max(MAX_ORTSPFAD).nullish(),
    beruf: z.string().trim().max(120).nullish(),
    kurzinfo: z.string().trim().max(200).nullish(),
    notiz: z.string().max(20000).nullish(),
    /**
     * Abweichende Namen. Fehlt das Feld, bleiben die vorhandenen stehen -
     * dieselbe Regel wie bei den Verweisen am Ereignis. Eine leere Liste
     * loescht sie dagegen, das ist der Unterschied zwischen „nicht gemeint"
     * und „keine mehr".
     */
    namen: z.array(zPersonenname).max(20).optional(),
});
export const zFamilieEingabe = z.object({
    bezeichnung: z.string().trim().max(120).nullish(),
    notiz: z.string().max(5000).nullish(),
});
export const zVerwandtschaftsart = z.enum(VERWANDTSCHAFTSARTEN);
/**
 * Eine neue Person anlegen und gleich neben eine bestehende haengen.
 * `familieId: null` bedeutet: neue Familie anlegen.
 */
export const zVerwandterEingabe = z.object({
    ausgehendVon: z.number().int().positive(),
    art: zVerwandtschaftsart,
    familieId: z.number().int().positive().nullable().default(null),
    person: zPersonEingabe,
});
export const zMitgliedEingabe = z.object({
    familieId: z.number().int().positive(),
    personId: z.number().int().positive(),
    rolle: zFamilienrolle,
    verbindungsart: zVerbindungsart.default("leiblich"),
    reihung: z.number().int().default(0),
});
/**
 * Ein Verweis auf eine Quelle im Netz - der Matricula-Scan des Taufbuchs,
 * ein Zeitungsartikel, ein Grabstein-Eintrag.
 *
 * Nur http und https. Das ist keine Förmlichkeit: `javascript:` in einem
 * Link, den später jemand anklickt, wäre eine offene Tür.
 */
export const zVerweis = z.object({
    titel: z.string().trim().min(1, "Bitte eine Beschriftung.").max(250),
    url: z
        .string()
        .trim()
        .max(2000)
        .url("Das sieht nicht nach einer Adresse aus.")
        .refine((u) => /^https?:\/\//i.test(u), "Nur Adressen mit http:// oder https://"),
    /** Wo genau in der Quelle - "Bd. 3, S. 42". */
    fundstelle: z.string().trim().max(250).nullish(),
});
/**
 * Ein Ereignis, so wie es die Maske schickt.
 *
 * Der Ort kommt als Name statt als ID: wer eine Hochzeit eintraegt, tippt
 * "Krems an der Donau" und soll nicht vorher einen Ortsdatensatz anlegen
 * muessen. Die API sucht den Ort und legt ihn bei Bedarf an.
 */
export const zEreignisEingabe = z.object({
    art: zEreignisart,
    datum: zUnscharfesDatum.nullish(),
    ortId: z.number().int().positive().nullish(),
    ortName: z.string().trim().max(MAX_ORTSPFAD).nullish(),
    /** Gesetzt bei Hochzeit, Scheidung, Jubilaeum - dann haengt das Ereignis an der Familie. */
    familieId: z.number().int().positive().nullish(),
    titel: z.string().trim().max(160).nullish(),
    beschreibung: z.string().max(5000).nullish(),
    beteiligte: z
        .array(z.object({
        personId: z.number().int().positive(),
        rolle: zEreignisrolle.default("hauptperson"),
    }))
        .min(1, "Mindestens eine beteiligte Person."),
    /** Verweise auf Quellen. Fehlt das Feld, bleiben die vorhandenen stehen. */
    verweise: z.array(zVerweis).max(20).optional(),
});
/**
 * Ein Bildausschnitt, relativ zur Bildgroesse (0..1).
 * Relativ, damit der Ausschnitt fuer jede abgeleitete Groesse gilt und
 * beim Neuberechnen der Vorschaubilder nicht verrutscht.
 */
export const zAusschnitt = z.object({
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    breite: z.number().min(0.001).max(1),
    hoehe: z.number().min(0.001).max(1),
});
export const zMedienTagEingabe = zAusschnitt.extend({
    personId: z.number().int().positive().nullish(),
    beschriftung: z.string().trim().max(120).nullish(),
});
/**
 * Angaben zum Bild selbst.
 *
 * Das Aufnahmedatum ist unscharf wie jedes andere Datum im Modell: ein
 * eingescanntes Foto ist "um 1950". Aus EXIF kommt beim Hochladen ein
 * exakter Wert, der hier überschrieben werden darf - was jemand weiß,
 * schlägt was die Kamera behauptet.
 */
export const zMedienEingabe = z.object({
    titel: z.string().trim().max(200).nullish(),
    beschreibung: z.string().max(5000).nullish(),
    aufnahme: zUnscharfesDatum.nullish(),
    ortId: z.number().int().positive().nullish(),
    ortName: z.string().trim().max(MAX_ORTSPFAD).nullish(),
});
export const zMedienVerknuepfung = z.object({
    zielTyp: zMedienziel,
    zielId: z.number().int().positive(),
});
/**
 * Ein neuer Zugang. OHNE Passwort - das setzt die Person selbst ueber den
 * Einladungslink, siehe `zPasswortSetzen`.
 */
export const zBenutzerEingabe = z.object({
    email: z.string().email(),
    anzeigename: z.string().trim().min(1).max(80),
    rolle: zBenutzerrolle.default("betrachter"),
    /** Person im Stammbaum, von der aus der Verwandtschaftsgrad gerechnet wird. */
    personId: z.number().int().positive().nullish(),
    /** Wie viele Schritte ueber Eltern/Kind-Kanten sichtbar sind. */
    maxBlutsgrad: z.number().int().min(0).max(12).default(3),
    /** Zaehlen angeheiratete Verwandte mit? */
    schwiegerSichtbar: z.boolean().default(true),
    /** Zusaetzliche Schritte ueber Partnerkanten. */
    maxSchwiegergrad: z.number().int().min(0).max(4).default(1),
    /** Sind laengst Verstorbene unabhaengig von der Verwandtschaft sichtbar? */
    verstorbeneSichtbar: z.boolean().default(true),
    /**
     * Derselbe Radius fuers Bearbeiten, immer <= dem Lesewert. Die API
     * begrenzt ihn beim Speichern; hier steht nur die aeussere Schranke.
     */
    bearbeitenBlutsgrad: z.number().int().min(0).max(12).default(0),
    bearbeitenSchwiegergrad: z.number().int().min(0).max(4).default(0),
    /** Verstorbene bearbeiten - Sperre, nicht Freigabe. */
    bearbeitenVerstorbene: z.boolean().default(true),
});
/**
 * Aenderung an einem Zugang. Jedes Feld einzeln optional: die Oberflaeche
 * schickt nur, was sich geaendert hat, und ein fehlendes Feld darf nie als
 * "auf Vorgabe zuruecksetzen" gelesen werden - sonst stellt ein Klick auf
 * "Rolle aendern" nebenbei den Sichtbarkeitsgrad zurueck.
 */
export const zBenutzerAendern = z.object({
    email: z.string().email().optional(),
    anzeigename: z.string().trim().min(1).max(80).optional(),
    rolle: zBenutzerrolle.optional(),
    personId: z.number().int().positive().nullable().optional(),
    maxBlutsgrad: z.number().int().min(0).max(12).optional(),
    schwiegerSichtbar: z.boolean().optional(),
    maxSchwiegergrad: z.number().int().min(0).max(4).optional(),
    verstorbeneSichtbar: z.boolean().optional(),
    bearbeitenBlutsgrad: z.number().int().min(0).max(12).optional(),
    bearbeitenSchwiegergrad: z.number().int().min(0).max(4).optional(),
    bearbeitenVerstorbene: z.boolean().optional(),
    aktiv: z.boolean().optional(),
    /**
     * Nur beim Aendern der EIGENEN E-Mail: das eigene Passwort zur
     * Bestaetigung. Die E-Mail ist der Anmeldename - ein Tippfehler darin
     * sperrt den letzten Besitzer aus, sobald seine Sitzung ablaeuft.
     */
    passwort: z.string().min(1).optional(),
});
export const zAnmeldung = z.object({
    email: z.string().email(),
    passwort: z.string().min(10, "Mindestens 10 Zeichen"),
});
/** Passwort ueber einen Einladungslink setzen (ohne Anmeldung). */
export const zPasswortSetzen = z.object({
    passwort: z.string().min(10, "Mindestens 10 Zeichen"),
});
/**
 * Eigenes Passwort aendern. Das bisherige wird mitverlangt: wer sich an
 * einem fremden, offen stehenden Browser zu schaffen macht, soll das
 * Passwort nicht einfach austauschen koennen.
 */
export const zPasswortAendern = z.object({
    alt: z.string().min(1),
    neu: z.string().min(10, "Mindestens 10 Zeichen"),
});
/** Was man an seinem eigenen Zugang selbst aendern darf. */
export const zIchAendern = z.object({
    anzeigename: z.string().trim().min(1).max(80),
});
//# sourceMappingURL=schemas.js.map